import { Button, Form, Spinner } from '@monksflow/monks-ui';
import { motion } from 'motion/react';
import {
  ReviewDetailsStep,
  UploadDocumentsStep,
  WIZARD_STEPS,
  useCreateProjectWizard,
} from '@/features/create-project';
import { usePageEntrance } from '@/shared/ui/animations';
import { ActionFooter } from '@/shared/ui/ActionFooter';
import {
  PageHeader,
  PageHeaderBreadcrumb,
  PageHeaderBreadcrumbLink,
  PageHeaderBreadcrumbPage,
  PageHeaderIdentity,
} from '@/shared/ui/layout';
import { StepTracker } from '@/shared/ui/StepTracker';
import { ROUTES } from '@/shared/config/routes';
import { Trans, useTranslation } from 'react-i18next';

export function NewProject() {
  const { t } = useTranslation('projects');
  const stepLabels = [t('newProject.intake'), t('newProject.details')];

  const wizard = useCreateProjectWizard();
  const entrance = usePageEntrance();

  return (
    <motion.div
      className="flex flex-col h-full gap-app-inset p-app-inset"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div {...entrance.header}>
        <PageHeader>
          <PageHeaderIdentity>
            <PageHeaderBreadcrumb>
              <PageHeaderBreadcrumbLink href={ROUTES.PROJECTS}>
                {t('newProject.projects')}
              </PageHeaderBreadcrumbLink>
              <PageHeaderBreadcrumbPage>{t('newProject.createProject')}</PageHeaderBreadcrumbPage>
            </PageHeaderBreadcrumb>
          </PageHeaderIdentity>
        </PageHeader>
      </motion.div>

      <motion.div
        data-testid="new-project-wizard"
        className="flex flex-1 flex-col bg-background rounded-lg overflow-hidden min-h-0"
        {...entrance.body}
      >
        <StepTracker activeStep={wizard.activeStepIndex} steps={stepLabels} />

        {wizard.isUploading ? (
          <div className="flex-1 flex flex-col items-center justify-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Spinner className="text-accent-foreground size-[49px]" />
            </motion.div>
            <motion.h2
              className="type-heading-sm text-foreground mt-6"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              {t('newProject.analyzingDocuments')}
            </motion.h2>
            <motion.p
              className="type-body-lg text-foreground-subtle text-center mt-4"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <Trans
                i18nKey="newProject.extractingKeyProjectInformation"
                ns="projects"
                components={{ br: <br /> }}
              />
            </motion.p>
            <motion.div
              className="mt-6"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <Button variant="outline" onClick={wizard.cancelOrAbort}>
                {t('newProject.cancel')}
              </Button>
            </motion.div>
          </div>
        ) : (
          <Form {...wizard}>
            <div className="flex-1 flex flex-col items-center overflow-auto min-h-0">
              <div className="flex flex-col items-center px-6 py-12 gap-2.5 w-full">
                {wizard.activeStepId === WIZARD_STEPS.intake.id && <UploadDocumentsStep />}
                {wizard.activeStepId === WIZARD_STEPS.details.id && <ReviewDetailsStep />}
              </div>
            </div>

            <div className="w-full px-section py-2 border-t border-border-light">
              <ActionFooter
                activeStep={wizard.activeStepIndex}
                totalSteps={stepLabels.length}
                enableNext={wizard.canContinue}
                onNext={() => void wizard.goNext()}
                onCancel={wizard.cancel}
                nextLabel={t('newProject.continue')}
                lastStepLabel={t('newProject.createProject')}
                onSkip={() => void wizard.skip()}
                skipLabel={t('newProject.skip')}
                maxWidth={650}
                isSubmitting={wizard.formState.isSubmitting}
              />
            </div>
          </Form>
        )}
      </motion.div>
    </motion.div>
  );
}
